import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Home, Calendar as CalendarIcon, FileText, Download, Eye, ChevronLeft, ChevronRight, Sparkles, Building2, Landmark, User } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Json } from "@/integrations/supabase/types";
import { Document, Page, pdfjs } from "react-pdf";
import { format } from "date-fns";
import { SealSettingsCard } from "./SealSettingsCard";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface MyUnaSettingsTabProps {
  userId: string;
  unaId: string | null;
}

interface BankAccountData {
  bank_name?: string;
  bank_address?: string;
  account_number?: string;
  opened_at?: string;
}

interface JourneyFormData {
  place_of_worship?: {
    address?: string;
    description?: string;
  };
  worship_cadence?: {
    frequency?: string;
  };
  una_meeting_cadence?: {
    frequency?: string;
    preferred_day?: string;
    start_date?: string;
  };
  observance_cadence?: {
    frequency?: string;
    start_date?: string;
  };
  bank_account?: string | BankAccountData;
  first_governance_meeting_date?: string;
  first_observance_meeting_date?: string;
  preferred_meeting_day?: string;
  unique_practices?: string;
  meeting_agenda?: string;
  board_meeting_agenda?: string;
}

interface UnaData {
  id: string;
  name: string;
  constitution_date: string | null;
  mission_statement: string | null;
  treasury_secretary_id: string | null;
  primary_officer_role: string | null;
}

const OFFICER_ROLES = [
  "President",
  "Vice President",
  "Secretary Treasurer",
  "Secretary",
  "Managing Member",
  "Board Member",
] as const;

const OBSERVANCE_FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'as needed', label: 'As Needed' },
];

const UNA_MEETING_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

export function MyUnaSettingsTab({ userId, unaId }: MyUnaSettingsTabProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // UNA Overview data
  const [unaData, setUnaData] = useState<UnaData | null>(null);
  const [treasurySecretaryName, setTreasurySecretaryName] = useState("");
  const [officerRole, setOfficerRole] = useState<string>("Secretary Treasurer");
  const [unaNameEdit, setUnaNameEdit] = useState("");
  const [constitutionDateEdit, setConstitutionDateEdit] = useState<Date | undefined>(undefined);
  
  // Bank account data
  const [bankAccount, setBankAccount] = useState<BankAccountData | null>(null);
  
  // User profile address (from profiles table - the source of truth)
  const [profileAddress, setProfileAddress] = useState("");
  const [profileCity, setProfileCity] = useState("");
  const [profileState, setProfileState] = useState("");
  const [profileZip, setProfileZip] = useState("");
  const [profileCountry, setProfileCountry] = useState("");
  
  // Form state - existing fields
  const [placeAddress, setPlaceAddress] = useState("");
  const [placeDescription, setPlaceDescription] = useState("");
  const [observanceFrequency, setObservanceFrequency] = useState("weekly");
  const [unaMeetingFrequency, setUnaMeetingFrequency] = useState("monthly");
  const [preferredMeetingDay, setPreferredMeetingDay] = useState("");
  const [firstGovernanceMeetingDate, setFirstGovernanceMeetingDate] = useState<string | null>(null);
  
  // Form state - NEW fields
  const [uniquePractices, setUniquePractices] = useState("");
  const [meetingAgenda, setMeetingAgenda] = useState("");
  const [boardMeetingAgenda, setBoardMeetingAgenda] = useState("");
  
  // Documents state
  const [constitutionDoc, setConstitutionDoc] = useState<{ file_url: string; file_name: string } | null>(null);
  
  // PDF Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const isPdf = constitutionDoc?.file_url?.toLowerCase().includes('.pdf') || 
                constitutionDoc?.file_name?.toLowerCase().endsWith('.pdf');
  
  useEffect(() => {
    if (userId) {
      loadAllData();
    }
  }, [userId, unaId]);

  const loadAllData = async () => {
    try {
      // Load user profile with address data (source of truth for address)
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('address, city, state, zip, country')
        .eq('id', userId)
        .single();
      
      if (userProfile) {
        setProfileAddress(userProfile.address || '');
        setProfileCity(userProfile.city || '');
        setProfileState(userProfile.state || '');
        setProfileZip(userProfile.zip || '');
        setProfileCountry(userProfile.country || 'US');
        
        // Build the full address string for the placeAddress field
        const addressParts = [
          userProfile.address,
          [userProfile.city, userProfile.state, userProfile.zip].filter(Boolean).join(', ')
        ].filter(Boolean);
        if (addressParts.length > 0) {
          setPlaceAddress(addressParts.join('\n'));
        }
      }

      // Load UNA data
      if (unaId) {
        const { data: una } = await supabase
          .from('unas')
          .select('id, name, constitution_date, mission_statement, treasury_secretary_id, primary_officer_role')
          .eq('id', unaId)
          .single();
        
        if (una) {
          setUnaData(una);
          setOfficerRole(una.primary_officer_role || 'Secretary Treasurer');
          setUnaNameEdit(una.name || '');
          if (una.constitution_date) {
            setConstitutionDateEdit(new Date(una.constitution_date));
          }
          
          // Load treasury secretary name
          if (una.treasury_secretary_id) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('full_name')
              .eq('id', una.treasury_secretary_id)
              .single();
            
            if (profile) {
              setTreasurySecretaryName(profile.full_name || '');
            }
          }
        }
      }

      // Load journey form data from user_journey_state
      const { data: journeyState, error: stateError } = await supabase
        .from('user_journey_state')
        .select('journey_form_data')
        .eq('user_id', userId)
        .single();

      if (stateError && stateError.code !== 'PGRST116') {
        console.error('Error loading journey state:', stateError);
      }

      if (journeyState?.journey_form_data) {
        const formData = journeyState.journey_form_data as JourneyFormData;
        
        // Parse bank account data
        if (formData.bank_account) {
          if (typeof formData.bank_account === 'string') {
            try {
              setBankAccount(JSON.parse(formData.bank_account));
            } catch {
              setBankAccount(null);
            }
          } else {
            setBankAccount(formData.bank_account);
          }
        }
        
        // Existing fields - place of worship becomes center for observance
        setPlaceAddress(formData.place_of_worship?.address || "");
        setPlaceDescription(formData.place_of_worship?.description || "");
        
        // Handle observance frequency - check multiple sources
        const obsFreq = formData.observance_cadence?.frequency || 
                       formData.worship_cadence?.frequency || 
                       "weekly";
        setObservanceFrequency(obsFreq.toLowerCase());
        
        // Handle UNA meeting frequency
        const unaFreq = formData.una_meeting_cadence?.frequency || "monthly";
        setUnaMeetingFrequency(unaFreq.toLowerCase());
        
        // Additional meeting data
        setPreferredMeetingDay(formData.preferred_meeting_day || formData.una_meeting_cadence?.preferred_day || "");
        setFirstGovernanceMeetingDate(formData.first_governance_meeting_date || formData.una_meeting_cadence?.start_date || null);
        
        // NEW fields
        setUniquePractices(formData.unique_practices || "");
        setMeetingAgenda(formData.meeting_agenda || "");
        setBoardMeetingAgenda(formData.board_meeting_agenda || "");
      }

      // Load constitution document directly from user_journey_documents table
      const { data: constitutionData } = await supabase
        .from('user_journey_documents')
        .select('file_url, document_name')
        .eq('user_id', userId)
        .eq('document_type', 'constitution')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (constitutionData) {
        setConstitutionDoc({
          file_url: constitutionData.file_url,
          file_name: constitutionData.document_name
        });
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save address to profiles table (source of truth)
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          address: profileAddress,
          city: profileCity,
          state: profileState,
          zip: profileZip,
          country: profileCountry,
        })
        .eq('id', userId);

      if (profileError) {
        console.error('Error updating profile address:', profileError);
        throw profileError;
      }

      // Build the full address string for journey_form_data
      const addressLine = [profileCity, profileState, profileZip].filter(Boolean).join(', ');
      const addressParts = [profileAddress, addressLine].filter(Boolean);
      if (profileCountry && profileCountry !== 'US') {
        addressParts.push(profileCountry);
      }
      const fullAddress = addressParts.join('\n');
      
      // Update placeAddress state for resolution updates
      setPlaceAddress(fullAddress);

      // Get existing form data first to preserve bank_account
      const { data: existingState } = await supabase
        .from('user_journey_state')
        .select('journey_form_data')
        .eq('user_id', userId)
        .single();

      const existingFormData = (existingState?.journey_form_data || {}) as JourneyFormData;

      const updatedFormData: JourneyFormData = {
        ...existingFormData,
        place_of_worship: {
          address: fullAddress,
          description: placeDescription,
        },
        worship_cadence: {
          frequency: observanceFrequency,
        },
        observance_cadence: {
          frequency: observanceFrequency,
        },
        una_meeting_cadence: {
          frequency: unaMeetingFrequency,
          preferred_day: preferredMeetingDay,
        },
        preferred_meeting_day: preferredMeetingDay,
        unique_practices: uniquePractices,
        meeting_agenda: meetingAgenda,
        board_meeting_agenda: boardMeetingAgenda,
      };

      // Update user_journey_state
      const { data: stateData } = await supabase
        .from('user_journey_state')
        .select('id')
        .eq('user_id', userId)
        .single();

      if (stateData) {
        const { error: updateError } = await supabase
          .from('user_journey_state')
          .update({
            journey_form_data: JSON.parse(JSON.stringify(updatedFormData)) as Json,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('user_journey_state')
          .insert([{
            user_id: userId,
            journey_form_data: JSON.parse(JSON.stringify(updatedFormData)) as Json,
            updated_at: new Date().toISOString()
          }]);

        if (insertError) throw insertError;
      }

      // Validate UNA name is provided
      if (!unaNameEdit.trim()) {
        toast.error('Please enter a UNA name');
        setSaving(false);
        return;
      }

      // Create or update UNA
      if (unaId) {
        // Update existing UNA
        const { error: unaUpdateError } = await supabase
          .from('unas')
          .update({ 
            name: unaNameEdit.trim(),
            constitution_date: constitutionDateEdit ? constitutionDateEdit.toISOString().split('T')[0] : null,
            primary_officer_role: officerRole,
            updated_at: new Date().toISOString()
          })
          .eq('id', unaId);

        if (unaUpdateError) {
          console.error('Error updating UNA:', unaUpdateError);
          throw unaUpdateError;
        }
      } else {
        // Create new UNA
        const { data: newUna, error: createError } = await supabase
          .from('unas')
          .insert({
            name: unaNameEdit.trim(),
            constitution_date: constitutionDateEdit ? constitutionDateEdit.toISOString().split('T')[0] : null,
            primary_officer_role: officerRole,
            treasury_secretary_id: userId,
            status: 'active'
          })
          .select('id, name, constitution_date, mission_statement, treasury_secretary_id, primary_officer_role')
          .single();

        if (createError) {
          console.error('Error creating UNA:', createError);
          throw createError;
        }

        // Update local state with new UNA data
        setUnaData(newUna);
        toast.success('UNA created successfully!');
      }

      // Update related resolutions if UNA exists (only for existing UNAs with resolutions)
      if (unaId) {
        // Update Center for Stewardship Observance resolution
        const { error: powError } = await supabase
          .from('una_resolutions')
          .update({
            implementation_notes: placeDescription || null,
            resolved_clauses: [
              `RESOLVED, that ${placeAddress || 'the designated location'} is hereby established as the official Center for Stewardship Observance;`,
              `RESOLVED, that this location shall serve as the primary meeting place for all religious assemblies and governance activities of the organization;`,
              `RESOLVED, that the Secretary Treasurer is authorized to take all necessary actions to formalize this designation.`
            ]
          })
          .eq('una_id', unaId)
          .ilike('resolution_title', '%Center for Stewardship Observance%');

        if (powError) console.error('Error updating Center for Stewardship Observance resolution:', powError);

        // Also try the old title for backwards compatibility
        await supabase
          .from('una_resolutions')
          .update({
            implementation_notes: placeDescription || null,
            resolved_clauses: [
              `RESOLVED, that ${placeAddress || 'the designated location'} is hereby established as the official Center for Stewardship Observance;`,
              `RESOLVED, that this location shall serve as the primary meeting place for all religious assemblies and governance activities of the organization;`,
              `RESOLVED, that the Secretary Treasurer is authorized to take all necessary actions to formalize this designation.`
            ]
          })
          .eq('una_id', unaId)
          .eq('resolution_title', 'Establishment of Place of Worship');

        // Update Meeting Cadence resolution
        const { error: mcError } = await supabase
          .from('una_resolutions')
          .update({
            resolved_clauses: [
              `RESOLVED, that observance gatherings shall be conducted on a ${observanceFrequency} basis;`,
              `RESOLVED, that UNA board meetings shall be conducted on a ${unaMeetingFrequency} basis;`,
              `RESOLVED, that the Secretary Treasurer shall maintain records of all meetings in accordance with governance requirements.`
            ]
          })
          .eq('una_id', unaId)
          .eq('resolution_title', 'Establishment of Regular Meeting Schedule');

        if (mcError) console.error('Error updating Meeting Cadence resolution:', mcError);

        // Update Unique Religious Practices resolution (if it exists)
        if (uniquePractices.trim()) {
          const { error: upError } = await supabase
            .from('una_resolutions')
            .update({
              implementation_notes: uniquePractices,
              resolved_clauses: [
                `RESOLVED, that the following unique religious practices are hereby recognized and declared as integral to the ecclesiastical mission: ${uniquePractices};`,
                `RESOLVED, that these practices shall be observed and incorporated into the religious life of the organization;`,
                `RESOLVED, that the Secretary Treasurer shall ensure these practices are documented in the governance records.`
              ]
            })
            .eq('una_id', unaId)
            .eq('resolution_title', 'Declaration of Unique Religious Practices');

          if (upError) console.error('Error updating Unique Religious Practices resolution:', upError);
        }

        // Update recurring tasks with new agendas if they exist
        if (meetingAgenda.trim()) {
          await supabase
            .from('custom_tasks')
            .update({
              task_description: `Conduct religious observance gathering.\n\nTypical Agenda:\n${meetingAgenda}`
            })
            .eq('una_id', unaId)
            .ilike('task_title', '%Religious%');
        }

        if (boardMeetingAgenda.trim()) {
          await supabase
            .from('custom_tasks')
            .update({
              task_description: `Conduct UNA governance board meeting.\n\nTypical Agenda:\n${boardMeetingAgenda}`
            })
            .eq('una_id', unaId)
            .eq('task_title', 'UNA Board Meeting');
        }
      }

      toast.success('UNA settings saved successfully');
    } catch (error) {
      console.error('Error saving UNA settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Helper to mask account number
  const maskAccountNumber = (accountNumber: string | undefined): string => {
    if (!accountNumber) return 'Not provided';
    if (accountNumber.length <= 4) return '****';
    return '****' + accountNumber.slice(-4);
  };

  // Helper to format date
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Not set';
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* UNA Overview Card - Always show, allows creation if no UNA exists */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            UNA Overview
          </CardTitle>
          <CardDescription>
            {unaData 
              ? "Your Unincorporated Nonprofit Association details"
              : "Set up your Unincorporated Nonprofit Association"
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="unaName" className="text-muted-foreground text-xs">UNA Name</Label>
              <Input
                id="unaName"
                value={unaNameEdit}
                onChange={(e) => setUnaNameEdit(e.target.value)}
                placeholder="Enter your UNA name"
                className="font-medium"
              />
              <p className="text-xs text-muted-foreground">
                This name appears on all generated documents
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs">Constitution Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-medium",
                      !constitutionDateEdit && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {constitutionDateEdit ? format(constitutionDateEdit, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={constitutionDateEdit}
                    onSelect={setConstitutionDateEdit}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                The date your UNA was constituted
              </p>
            </div>
          </div>
          {unaData?.mission_statement && (
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Mission Statement</Label>
              <p className="text-sm">{unaData.mission_statement}</p>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="officerRole">Your Role in the UNA</Label>
              <Select value={officerRole} onValueChange={setOfficerRole}>
                <SelectTrigger id="officerRole">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  {OFFICER_ROLES.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Your official title within the UNA
              </p>
            </div>
            
            {treasurySecretaryName && (
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg h-fit">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Account Holder</p>
                  <p className="font-medium">{treasurySecretaryName}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Banking Information Card */}
      {bankAccount && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5" />
              Banking Information
            </CardTitle>
            <CardDescription>
              Bank account details recorded during your onboarding journey
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Bank Name</Label>
                <p className="font-medium">{bankAccount.bank_name || 'Not provided'}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Account Number</Label>
                <p className="font-medium font-mono">{maskAccountNumber(bankAccount.account_number)}</p>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Bank Address</Label>
              <p className="text-sm">{bankAccount.bank_address || 'Not provided'}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Account Opened</Label>
              <p className="text-sm">{formatDate(bankAccount.opened_at)}</p>
            </div>
            <p className="text-xs text-muted-foreground italic">
              This information was recorded during the bank account setup step and is stored securely.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Center for Stewardship Observance Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="h-5 w-5" />
            Center for Stewardship Observance
          </CardTitle>
          <CardDescription>
            Your designated center for religious assembly and stewardship observance
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="profileAddress">Street Address</Label>
            <Input
              id="profileAddress"
              placeholder="123 Main Street"
              value={profileAddress}
              onChange={(e) => setProfileAddress(e.target.value)}
            />
          </div>

            <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-2">
              <Label htmlFor="profileCity">City</Label>
              <Input
                id="profileCity"
                placeholder="City"
                value={profileCity}
                onChange={(e) => setProfileCity(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="profileState">State / Province</Label>
              <Input
                id="profileState"
                placeholder="State"
                value={profileState}
                onChange={(e) => setProfileState(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="profileZip">ZIP / Postal Code</Label>
              <Input
                id="profileZip"
                placeholder="12345"
                value={profileZip}
                onChange={(e) => setProfileZip(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="profileCountry">Country</Label>
            <Input
              id="profileCountry"
              placeholder="US"
              value={profileCountry}
              onChange={(e) => setProfileCountry(e.target.value)}
            />
          </div>
          
          <p className="text-xs text-muted-foreground">
            This address is used for UNA documents and will be your Center for Stewardship Observance location.
          </p>
          
          <div className="grid gap-2">
            <Label htmlFor="placeDescription">Description</Label>
            <Textarea
              id="placeDescription"
              placeholder="Describe the significance of this location (optional)"
              value={placeDescription}
              onChange={(e) => setPlaceDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="observanceFrequency">Observance Gathering Frequency</Label>
            <Select value={observanceFrequency} onValueChange={setObservanceFrequency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OBSERVANCE_FREQUENCY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              How often does your family gather for observance?
            </p>
          </div>
        </CardContent>
      </Card>

      {/* UNA Meeting Schedule Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            UNA Board Meetings
          </CardTitle>
          <CardDescription>
            Schedule for official UNA governance meetings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="unaMeetingFrequency">Board Meeting Frequency</Label>
            <Select value={unaMeetingFrequency} onValueChange={setUnaMeetingFrequency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UNA_MEETING_FREQUENCY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              How often does your UNA board meet for governance activities?
            </p>
          </div>
          
          {preferredMeetingDay && (
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Preferred Meeting Day</Label>
              <p className="font-medium">{preferredMeetingDay}</p>
            </div>
          )}
          
          {firstGovernanceMeetingDate && (
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">First Governance Meeting</Label>
              <p className="font-medium">{formatDate(firstGovernanceMeetingDate)}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Religious Practices & Agendas Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Religious Practices & Meeting Agendas
          </CardTitle>
          <CardDescription>
            Document your unique religious practices and typical meeting activities
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="uniquePractices">Unique Religious Practices</Label>
            <Textarea
              id="uniquePractices"
              placeholder="Share any special faith practices, feast days, fasting times, or prayer routines your family keeps."
              value={uniquePractices}
              onChange={(e) => setUniquePractices(e.target.value)}
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              These practices are documented in your governance records as part of your ecclesiastical identity.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="meetingAgenda">Observance Gathering Agenda</Label>
            <Textarea
              id="meetingAgenda"
              placeholder="Walk through what your gatherings usually look like: prayer, scripture, songs, sharing, meal."
              value={meetingAgenda}
              onChange={(e) => setMeetingAgenda(e.target.value)}
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              This agenda is included in your recurring observance gathering tasks.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="boardMeetingAgenda">Board Meeting Agenda</Label>
            <Textarea
              id="boardMeetingAgenda"
              placeholder="What your board usually covers: money check-in, decisions to sign, stewardship talk, plans for the season."
              value={boardMeetingAgenda}
              onChange={(e) => setBoardMeetingAgenda(e.target.value)}
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              This agenda is included in your recurring "UNA Board Meeting" task.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Documents Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Founding Documents
          </CardTitle>
          <CardDescription>
            Documents uploaded during your onboarding journey
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {constitutionDoc ? (
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-primary" />
                <div>
                  <p className="font-medium">Constitution</p>
                  <p className="text-sm text-muted-foreground">{constitutionDoc.file_name}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewOpen(true)}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                >
                  <a href={constitutionDoc.file_url} download={constitutionDoc.file_name}>
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </a>
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-muted/50 rounded-lg border text-center">
              <p className="text-muted-foreground">No constitution uploaded yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Complete your onboarding journey to upload your constitution
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Seal Card */}
      <SealSettingsCard unaId={unaId} />

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save UNA Settings
        </Button>
      </div>

      {/* PDF Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{constitutionDoc?.file_name || 'Constitution'}</DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col items-center">
            {isPdf ? (
              <>
                <Document
                  file={constitutionDoc?.file_url}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<Loader2 className="h-8 w-8 animate-spin" />}
                  error={<div className="text-destructive">Failed to load PDF</div>}
                >
                  <Page 
                    pageNumber={pageNumber} 
                    width={Math.min(800, window.innerWidth - 100)}
                  />
                </Document>
                
                {numPages && numPages > 1 && (
                  <div className="flex items-center gap-4 mt-4">
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(p => Math.max(1, p - 1))} 
                      disabled={pageNumber <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">Page {pageNumber} of {numPages}</span>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(p => Math.min(numPages, p + 1))} 
                      disabled={pageNumber >= numPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <img 
                src={constitutionDoc?.file_url} 
                alt="Constitution" 
                className="max-w-full rounded-lg"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
